<template>
  <div>
    <ui-dialog :show="isOpen" max-width="800" :closable="false">
      <template #header>
        <h3>
          {{ $t("message.reviews.tags.reviewTags") }}
        </h3>
      </template>

      <div class="tags-preview-dialog">
        <ReviewPreview :review="review" />
        <div class="tags-preview-dialog__tags-container">
          <div class="tags-preview-dialog__search-container">
            <div class="tags-preview-dialog__section-title">
              {{ $t("message.reviews.tags.selectTag") }}
            </div>
            <v-text-field
              v-model="search"
              :label="$t('message.common.find')"
              append-icon="icon-search"
              rounded
              outlined
              hide-details
              solo
              dense
              class="tags-preview-dialog__search"
            />
          </div>

          <vue-perfect-scrollbar
            class="tags-preview-dialog__scrollable-container"
          >
            <ReviewTagsSelector
              v-if="tags"
              :show-header="false"
              :available-tags="availableTags"
              :review="review"
              :is-loading="false"
            />
          </vue-perfect-scrollbar>
        </div>
      </div>
      <template #actions>
        <div class="tags-preview-dialog__actions">
          <InfoBox
            :text="$t('message.reviews.tags.changesSavedAutomatically')"
          />
          <ui-button outlined @click="handleClose">{{
            $t("message.common.close")
          }}</ui-button>
        </div>
      </template>
    </ui-dialog>
  </div>
</template>

<script>
import ReviewPreview from "./ReviewPreview.vue";
import ReviewTagsSelector from "./ReviewTagsSelector.vue";
import InfoBox from "@/components/ui/InfoBox.vue";

export default {
  name: "TagsPreviewDialog",

  components: {
    ReviewPreview,
    ReviewTagsSelector,
    InfoBox
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    review: {
      type: Object,
      default: () => ({})
    },
    tags: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      search: ""
    };
  },

  computed: {
    availableTags() {
      return [...this.activeTags, ...this.inactiveTags];
    },
    filteredTags() {
      return this.tags.filter(tag =>
        tag.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    activeTags() {
      return this.filteredTags.filter(
        tag => this.review.tags && this.review.tags.some(t => t.id === tag.id)
      );
    },
    inactiveTags() {
      return this.filteredTags.filter(
        tag => !this.review.tags || !this.review.tags.some(t => t.id === tag.id)
      );
    }
  },

  methods: {
    handleClose() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.tags-preview-dialog {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;

  &__search-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: $spacing * 2;
    margin-bottom: $spacing * 2;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
  }

  &__search {
    max-width: 60%;

    ::v-deep .v-input__slot {
      padding-left: $spacing * 2;
      padding-right: $spacing * 2;
      box-shadow: none !important;
    }

    ::v-deep .v-label {
      top: 10px !important;
      @include font-size(14px);
    }

    ::v-deep .v-input__append-inner {
      margin-top: 0 !important;
    }
  }

  &__tags-container {
    margin-top: $spacing * 3;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__scrollable-container {
    padding-top: 0;
    max-height: 300px;
    overflow: hidden;
  }

  &__section-title {
    @include font-size(16px);
    font-weight: 500;
    color: $grey-darker;
    position: relative;
    padding-left: $spacing * 2;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 0;
      width: 3px;
      height: 32px;
      transform: translateY(-50%);
      background-color: #dbda1f;
    }
  }
}

::v-deep {
  .tags-list-container {
    margin: 0;
  }

  .section-header {
    position: sticky;
    top: 0;
    background: white;

    &__title {
      margin-top: 0;
    }
  }

  .tags-list {
    padding: 0;
  }

  .v-expansion-panel-header {
    padding: 0;
    min-height: 32px;
    height: 32px;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    color: $grey-darker;
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;

    .v-expansion-panel-header__icon {
      margin-left: unset;
      margin-right: $spacing;
    }
  }

  .v-expansion-panel-content__wrap {
    padding: 0;
  }

  .v-expansion-panel--active:not(:first-child),
  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0 !important;
  }

  .v-card__text {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}
</style>
